import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';
import { toastError, toastSuccess } from '@components/Toast';

import { useSetPoAutmationMutation, useSetExtractPoMutation } from '@features/order/api/PoAutomationApi';
import { isAllowedPoFile, poDocMimeTypeForFile } from '@features/collection/techpack-journey/utils';
import { useUploadFileToS3Mutation } from '@features/collection/techpack-journey/api/MultipleTechpackJourneyApi';

import { storeProductData } from '@store/actions/DesignAction';

import CommonImage from '@/components/CommonImage';
import { ReactComponent as UploadIcon } from '@/assets/icons/upload-gray.svg';
import LoaderComponent from '@/components/LoaderComponent';

const PoAutomationPlaceModal = ({ onClose, collectionId, folderId }) => {
	const [setPoAutmation, { isLoading: isInitiatingLoading }] = useSetPoAutmationMutation();
	const [setExtractPo, { isLoading: isExtractingLoading }] = useSetExtractPoMutation();
	const [uploadFileToS3, { isLoading: isUploadingToS3 }] = useUploadFileToS3Mutation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [files, setFiles] = useState([]);
	const [isDragging, setIsDragging] = useState(false);
	const uploadInputRef = useRef(null);
	const isLoading = isInitiatingLoading || isExtractingLoading || isUploadingToS3;

	const extractPoDocuments = async (documentIds) => {
		const response = await setExtractPo({
			requestBody: {
				documentIds,
				...(collectionId ? { collectionId } : { folderId: folderId || 0 })
			}
		}).unwrap();
		dispatch(storeProductData('poAutomationResponse', response));
		return response;
	};

	/// file upload process to initiate
	const onAddFiles = async (selectedList) => {
		const selectedFiles = Array.from(selectedList || []);
		if (selectedFiles.length === 0) return;

		const allowedFiles = selectedFiles.filter(isAllowedPoFile);
		if (allowedFiles.length === 0) {
			toastError('Only PDF, JPEG, PNG, DOC, DOCX, XLS, and XLSX files are allowed.');
			return;
		}

		const newEntries = allowedFiles.map((file, idx) => ({
			id: `${file.name}-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 9)}`,
			name: file.name,
			docMimeType: poDocMimeTypeForFile(file),
			rawFile: file,
			documentId: null
		}));

		setFiles((prev) => [...prev, ...newEntries]);

		try {
			const requestBody = {
				collectionId: collectionId || folderId || 0,
				documentDTOList: allowedFiles.map((file) => ({
					name: file.name,
					docMimeType: poDocMimeTypeForFile(file),
					documentType: 'PURCHASE_ORDER'
				}))
			};

			const initiatedDocs = await setPoAutmation({ requestBody }).unwrap();

			await Promise.all(
				allowedFiles.map(async (file, index) => {
					const entryId = newEntries[index]?.id;
					const doc = initiatedDocs[index];
					const url = doc?.urlList?.[0];
					if (!url) return;
					try {
						await uploadFileToS3({ url, file }).unwrap();
						setFiles((prev) =>
							prev.map((item) =>
								item.id === entryId ? { ...item, documentId: doc?.documentId ?? null } : item
							)
						);
					} catch {
						// Keep row visible; user can remove/re-upload if S3 upload fails.
					}
				})
			);
		} catch (error) {
			toastError(error?.data?.message || 'Failed to initiate upload.');
		}
	};

	const handleFileSelect = (e) => {
		onAddFiles(e.target.files);
		e.target.value = '';
	};

	const onOpenFileDialog = () => uploadInputRef.current?.click();

	const onDragOver = (e) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const onDragLeave = () => setIsDragging(false);

	const onDrop = (e) => {
		e.preventDefault();
		setIsDragging(false);
		onAddFiles(e.dataTransfer.files);
	};

	const handleRemoveFile = (index) => {
		setFiles((prev) => prev.filter((_, i) => i !== index));
	};

	// Submit only triggers extract on already uploaded documents
	const handleSubmit = async () => {
		if (files.length === 0) {
			toastError('Please upload at least one file');
			return;
		}

		try {
			const documentIds = files
				.map((file) => file.documentId)
				.filter((id) => id !== null && id !== undefined);
			if (documentIds.length === 0) {
				toastError('No uploaded files found. Please upload files first.');
				return;
			}
			// Call the extract PO endpoint with the uploaded document IDs
			const extractResponse = await extractPoDocuments(documentIds);

			// Also save to localStorage for refresh persistence
			if (typeof window !== 'undefined') {
				localStorage.setItem('poAutomationData', JSON.stringify(extractResponse));
			}

			toastSuccess(extractResponse?.message || 'PO uploaded successfully');
			setFiles([]);
			onClose();

			// Navigate to PoAutomationDetails with data already in Redux/localStorage
			navigate(`/orders/po-automation`, {
				state: {
					data: extractResponse,
					collectionId: collectionId,
					poDocIdList: documentIds,
					folderId: folderId
				}
			});
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};
	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="width-784-dialog">
				<div className="common-popup filter-quick-search-modal">
					<div className="common-popup-header d-flex justify-content-between">
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">PO Automation</h3>
						</div>
						<div className="close-btn" onClick={onClose}>
							<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
						</div>
					</div>
					<div
						className="common-popup-body multiple-upload-techpack-page w-100 scroll-y-label"
						style={{ minHeight: 'auto' }}
					>
						<div className="row">
							<div className="col-md-12">
								<div
									className={`techpack-dropzone ${isDragging ? 'dragging' : ''}`}
									onClick={onOpenFileDialog}
									onDragOver={onDragOver}
									onDragLeave={onDragLeave}
									onDrop={onDrop}
								>
									<input
										ref={uploadInputRef}
										type="file"
										accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.doc,.docx,.xls,.xlsx"
										multiple
										onChange={handleFileSelect}
										className="d-none"
									/>
									<div className="upload-arrow mb-2">
										<UploadIcon width={34} height={34} />
									</div>
									<p className="dropzone-title medium-14 gray_dark_02">
										Click to upload or drag and drop
									</p>
									<p className="dropzone-hint medium-14 gray_dark_02">
										Upload PDF, JPEG, PNG, DOC, DOCX, XLS, or XLSX files
									</p>
								</div>
							</div>

							{files.length > 0 && (
								<div className="col-md-12 mt-3">
									<div className="upload-file-card">
										<div className="upload-card-header">
											<p className="upload-count regular-14 gray_dark_01">
												files: {files.length}
											</p>
										</div>
										<div className="techpack-file-list">
											{files.map((file, index) => (
												<div
													key={file.id || index}
													className="single-pdf-file techpack-file-row d-flex align-items-center justify-content-start mr-3 mb-2 position-relative"
												>
													<CommonImage
														src={
															file.docMimeType?.includes('pdf')
																? '/images/file.svg'
																: '/icons/file.svg'
														}
														width="24"
													/>
													<p className="regular-14 gray_dark_01 pl-2 mb-0">
														{file.name}
													</p>
													<div
														className="action-img cursor-pointer position-absolute"
														style={{ right: '-20px' }}
														onClick={() => handleRemoveFile(index)}
													>
														<CommonImage
															src="/icons/delete_red.svg"
															width="16"
															height="16"
														/>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					<div className="modal-footer">
						<div className="footer-buttons text-right">
							{/* <button
								className="button gray-outline size32"
								onClick={onClose}
								disabled={isLoading}
							>
								Cancel
							</button> */}
							<button
								className="button size32 ml-2"
								onClick={handleSubmit}
								disabled={isLoading || files.length === 0}
							>
								{isLoading ? 'Uploading...' : 'Submit'}
							</button>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default PoAutomationPlaceModal;
